import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { handleFormErrors } from "../../utils/helpers";

// ******* Queries *******

export const useGetUsersQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  // Build query parameters
  const queryParams = new URLSearchParams();

  // Add search parameter if provided
  if (params.search && params.search.trim()) {
    queryParams.append("search", params.search.trim());
  }

  // Add other parameters if needed
  Object.keys(params).forEach((key) => {
    if (
      key !== "search" &&
      params[key] !== undefined &&
      params[key] !== null &&
      params[key] !== ""
    ) {
      queryParams.append(key, params[key]);
    }
  });

  const queryString = queryParams.toString();
  const url = queryString ? `users?${queryString}` : "users";

  return useQuery({
    queryKey: ["users", params],
    queryFn: () => axiosProtected.get(url),
  });
};
export const useGetOneUserQuery = ({ id }) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useQuery({
    queryKey: ["one-user", id],
    queryFn: () => axiosProtected.get(`users/${id}`),
    enabled: !!id,
    onSuccess: () => {
      toast.success(
        t("USER_UPDATED_SUCCESSFULLY") || "User updated successfully"
      );
    },
    onError: (err) => {
    },
  });
};
// ******* Mutations *******
export const useUserUpdateMutation = ({ setError }) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, ...payload }) => {
      const formData = new FormData();
      formData.append("_method", "PUT");
      Object.keys(payload).forEach((key) => {
        const value = payload[key];

        if (key === "service_center_id") {
          formData.append(key, value ?? "");
          return;
        }

        if (value !== null && value !== undefined) {
          // Special handling for profile_image
          if (key === "profile_image" && value?.originFileObj) {
            formData.append(key, value.originFileObj);
          } else if (key === "profile_image" && value instanceof File) {
            formData.append(key, value);
          } else if (key === "system_ids" && Array.isArray(value)) {
            value.forEach((id) => {
              formData.append("system_ids[]", id);
            });
          } else if (key === "group_ids" && Array.isArray(value)) {
            // Handle group_ids as array
            value.forEach((id) => {
              formData.append("group_ids[]", id);
            });
          } else if (key !== "profile_image") {
            formData.append(key, value);
          }
          // Skip profile_image if it's not a valid file
        }
      });

      return axiosProtected.post(`users/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    mutationKey: "userUpdate",
    onSuccess: () => {
      toast.success(
        t("USER_UPDATED_SUCCESSFULLY") || "User updated successfully"
      );
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "USER_UPDATED_FAILED");
    },
  });
};
export const useCreateUserMutation = (setError) => {
  const navigate = useNavigate();
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload) => {
      const formData = new FormData();
      Object.keys(payload).forEach((key) => {
        const value = payload[key];
        if (value !== null && value !== undefined) {
          if (key === "profile_image" && value?.originFileObj) {
            formData.append(key, value.originFileObj);
          } else if (key === "profile_image" && value instanceof File) {
            formData.append(key, value);
          } else if (key === "system_ids" && Array.isArray(value)) {
            value.forEach((id) => {
              formData.append("system_ids[]", id);
            });
          } else if (key === "group_ids" && Array.isArray(value)) {
            // Handle group_ids as array
            value.forEach((id) => {
              formData.append("group_ids[]", id);
            });
          } else if (key !== "profile_image") {
            formData.append(key, value);
          }
        }
      });
      return axiosProtected.post("users/create-user", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    mutationKey: "createUser",
    onSuccess: () => {
      toast.success(
        t("USER_CREATED_SUCCESSFULLY") || "User created successfully"
      );
      navigate("/app/user_management/users");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "USER_CREATION_FAILED");
    },
  });
};
export const useDeleteUserMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`users/${id}`),
    mutationKey: "deleteUser",
    onSuccess: () => {
      toast.success(
        t("USER_DELETED_SUCCESSFULLY") || "User deleted successfully"
      );
    }
  });
};
export const useUserStatusUpdateMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }) =>
      axiosProtected.post(`users/${id}/status`, {
        status: is_active ? "inactive" : "active",
      }),
    mutationKey: "suspendUser",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(
        t("USER_STATUS_UPDATED_SUCCESSFULLY") || "User suspended successfully"
      );
    }
  });
};
export const useUploadProfileImageMutation = () => {
  const { axiosProtected } = useAxios();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ user_id, profile_image }) => {
      const formData = new FormData();

      if (profile_image?.originFileObj) {
        formData.append("profile_image", profile_image.originFileObj);
      } else if (profile_image instanceof File) {
        formData.append("profile_image", profile_image);
      }

      return axiosProtected.post(`users/${user_id}/upload-profile`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    mutationKey: "uploadProfileImage",
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users", variables.user_id] });

      toast.success(
        t("USER_UPLOAD_PROFILE_IMAGE_SUCCESSFULLY") ||
          "User uploaded profile image successfully"
      );
    }
  });
};
export const useResetPasswordMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload) => axiosProtected.post("reset-password", payload),
    mutationKey: "updatePassword",
    onSuccess: () => {
      toast.success(
        t("PASSWORD_UPDATED_SUCCESSFULLY") || "Password updated successfully"
      );
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "PASSWORD_UPDATED_FAILED");
    },
  });
};
export const useChangePasswordMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload) =>
      axiosProtected.post("profile/change-password", payload),
    mutationKey: "changePassword",
    onSuccess: () => {
      toast.success(
        t("PASSWORD_CHANGED_SUCCESSFULLY") || "Password changed successfully"
      );
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "PASSWORD_CHANGED_FAILED");
    },
  });
};
export const useResetUserPasswordMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ user_id }) =>
      axiosProtected.put(`users/${user_id}/reset-password`),
    mutationKey: "resetUserPassword",

    onError: (err) => {
      handleFormErrors(err, setError, t, "USER_PASSWORD_RESET_FAILED");
    },
  });
};
